import BrandLogo from '../common/BrandLogo';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { useEffect, useState } from 'react';
import api from '../../services/api/api';
import { logout } from '../../store/slices/authSlice';
import {
  LayoutDashboard,
  User,
  Calendar,
  FileText,
  IndianRupee,
  Settings,
  Users,
  Briefcase,
  Shield,
  UserPlus,
  BarChart3,
  Key,
  Database,
  Activity,
  CheckCircle,
  BookOpen,
  Package,
  TrendingUp,
  Search,
  FileCheck,
  Code,
  Megaphone,
  Headphones,
  Building2,
  FolderKanban,
  GitBranch,
  Bug,
  Rocket,
  Palette,
  Ticket,
  Clock,
  AlertCircle,
  Lock,
  UserCheck,
  ClipboardList,
  ChevronDown,
  LogOut,
} from 'lucide-react';
import { GLOBAL_ADMIN_MENU_TAG, isGlobalAdminUser } from '../../utils/access';
import { ResumeCreationSidebarDropdown } from '../../departments/resumeMarketing/components/ResumeCreationSidebarDropdown';

const Sidebar = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { user } = useSelector((state) => state.auth);
  const [employeeDepartment, setEmployeeDepartment] = useState(null);
  const [departmentResolved, setDepartmentResolved] = useState(false);
  const [breakLocked, setBreakLocked] = useState(false);
  const [resumeTeamMgmtOpen, setResumeTeamMgmtOpen] = useState(false);
  const [resumeMyWorkOpen, setResumeMyWorkOpen] = useState(false);
  const [talentTeamMgmtOpen, setTalentTeamMgmtOpen] = useState(false);
  const [talentMyWorkOpen, setTalentMyWorkOpen] = useState(false);
  const [resumeMarketingReportsOpen, setResumeMarketingReportsOpen] = useState(false);
  const [resumeCreationOpen, setResumeCreationOpen] = useState(false);
  const [genericTlTeamMgmtOpen, setGenericTlTeamMgmtOpen] = useState(false);
  const [devMyWorkOpen, setDevMyWorkOpen] = useState(false);
  const [adminGroupOpen, setAdminGroupOpen] = useState({
    adminManagement: true,
    resumeMarketing: true,
    talentTeam: true,
    hrManagement: true,
  });

  const isPathActive = (path) =>
    location.pathname === path || location.pathname.startsWith(`${path}/`);

  useEffect(() => {
    const roleName = user?.role?.name;
    if (roleName === 'Employee' || roleName === 'TL') {
      const cacheKey = `sidebar_department_v1_${user?.employee?._id || user?._id || 'me'}`;
      const cachedDept = sessionStorage.getItem(cacheKey);
      if (cachedDept) {
        setEmployeeDepartment(cachedDept);
        setDepartmentResolved(true);
      }

      api
        .get('/employee/profile')
        .then((response) => {
          const profile = response.data.data || {};
          const deptName = profile?.department?.name?.toLowerCase() || '';
          setEmployeeDepartment(deptName);
          if (deptName) sessionStorage.setItem(cacheKey, deptName);
        })
        .catch(() => {
          // Silently fail - will just show generic dashboard
        })
        .finally(() => {
          setDepartmentResolved(true);
        });
    } else {
      setDepartmentResolved(true);
    }
  }, [user?.role?.name, user?._id, user?.employee?._id]);

  useEffect(() => {
    const refreshBreakState = async () => {
      try {
        const response = await api.get('/attendance/current-session');
        const s = response?.data?.data;
        const locked = Boolean(s?.checkIn && !s?.checkOut && s?.activeBreak?.startedAt);
        setBreakLocked(locked);
      } catch {
        setBreakLocked(false);
      }
    };

    refreshBreakState();
    const timer = setInterval(refreshBreakState, 30000);
    const onSessionUpdate = () => {
      refreshBreakState();
    };
    window.addEventListener('attendance-session-updated', onSessionUpdate);
    return () => {
      clearInterval(timer);
      window.removeEventListener('attendance-session-updated', onSessionUpdate);
    };
  }, []);

  // Determine if employee belongs to a department that has its own dashboard
  const isTalentEmployee = employeeDepartment && 
    (employeeDepartment.includes('talent') || employeeDepartment.includes('recruitment'));
  const isDevelopmentEmployee = employeeDepartment && 
    (employeeDepartment.includes('development') || employeeDepartment.includes('engineering') || employeeDepartment.includes('dev'));
  const isDigitalMarketingEmployee = employeeDepartment && 
    (employeeDepartment.includes('digital') && employeeDepartment.includes('marketing'));
  const isResumeMarketingEmployee = employeeDepartment && 
    (employeeDepartment.includes('resume') && employeeDepartment.includes('marketing'));
  const isTechnicalSupportEmployee = employeeDepartment && 
    (employeeDepartment.includes('technical') && employeeDepartment.includes('support'));
  const isFinanceEmployee = employeeDepartment && 
    (employeeDepartment.includes('finance') || employeeDepartment.includes('accounting'));
  const isHREmployee = employeeDepartment && 
    (employeeDepartment.includes('hr') || employeeDepartment.includes('human'));

  const isDepartmentEmployee = isTalentEmployee || isDevelopmentEmployee || isDigitalMarketingEmployee || 
                                isResumeMarketingEmployee || isTechnicalSupportEmployee || 
                                isFinanceEmployee || isHREmployee;

  // Marketing TL only (Resume Making, Assigned Candidates) – not Talent TL
  const isMarketingTL = user?.role?.name === 'TL' && employeeDepartment &&
    ((employeeDepartment.includes('resume') && employeeDepartment.includes('marketing')) ||
     (employeeDepartment.includes('digital') && employeeDepartment.includes('marketing')));
  const isResumeMarketingTL = user?.role?.name === 'TL' && employeeDepartment &&
    employeeDepartment.includes('resume') && employeeDepartment.includes('marketing');

  // Talent TL – show Candidate Report & Daily Records
  const isTalentTL = user?.role?.name === 'TL' && employeeDepartment &&
    (employeeDepartment.includes('talent') || employeeDepartment.includes('recruitment'));

  // Development TL – show Development TL Dashboard
  const isDevelopmentTL = user?.role?.name === 'TL' && employeeDepartment &&
    (employeeDepartment.includes('development') || employeeDepartment.includes('engineering') || employeeDepartment.includes('dev'));

  const isGenericTL =
    user?.role?.name === 'TL' && !isResumeMarketingTL && !isTalentTL;

  useEffect(() => {
    if (!isResumeMarketingTL) {
      setResumeTeamMgmtOpen(false);
      setResumeMyWorkOpen(false);
      setResumeCreationOpen(false);
    } else {
      setResumeTeamMgmtOpen([
        '/tl/team',
        '/tl/attendance',
        '/resume-marketing/assigned-candidates',
        '/resume-marketing/resume-tracking',
        '/tl/tasks',
        '/tl/approvals',
        '/tl/performance',
        '/tl/productivity',
        '/tl/escalations',
      ].includes(location.pathname) ||
        location.pathname.startsWith('/resume-marketing/assigned-candidates/') ||
        location.pathname.startsWith('/resume-marketing/resume-tracking/'));

      setResumeMyWorkOpen([
        '/employee/profile',
        '/resume-marketing/client-submissions',
        '/resume-marketing/interview-tracking',
      ].includes(location.pathname) ||
        location.pathname.startsWith('/resume-marketing/client-submissions/') ||
        location.pathname.startsWith('/resume-marketing/interview-tracking/'));
    }

    if (!isTalentTL) {
      setTalentTeamMgmtOpen(false);
      setTalentMyWorkOpen(false);
    } else {
      setTalentTeamMgmtOpen([
        '/tl/team',
        '/tl/attendance',
        '/talent/interviews',
        '/tl/tasks',
        '/tl/approvals',
        '/tl/performance',
        '/tl/productivity',
        '/tl/reports',
        '/tl/escalations',
      ].includes(location.pathname) ||
        location.pathname.startsWith('/talent/interviews/'));

      setTalentMyWorkOpen([
        '/employee/profile',
        '/talent/candidates',
        '/talent/daily-records',
      ].includes(location.pathname) ||
        location.pathname.startsWith('/talent/candidates/') ||
        location.pathname.startsWith('/talent/daily-records/'));
    }
  }, [location.pathname, isResumeMarketingTL, isTalentTL]);

  useEffect(() => {
    if (!isGenericTL) {
      setGenericTlTeamMgmtOpen(false);
      return;
    }
    const basePaths = [
      '/tl/team',
      '/tl/attendance',
      '/tl/tasks',
      '/tl/approvals',
      '/tl/performance',
      '/tl/escalations',
      '/tl/productivity',
      '/tl/reports',
      '/talent/interviews',
    ];
    const open = [...basePaths].some(
      (p) => location.pathname === p || location.pathname.startsWith(`${p}/`)
    );
    setGenericTlTeamMgmtOpen(open);
  }, [location.pathname, isGenericTL]);

  useEffect(() => {
    const devMyWorkPaths = [
      '/development/projects',
      '/development/sprints',
      '/development/bugs',
      '/development/code-reviews',
      '/development/deployments',
    ];
    const open = devMyWorkPaths.some(
      (p) => location.pathname === p || location.pathname.startsWith(`${p}/`)
    );
    setDevMyWorkOpen(open);
  }, [location.pathname]);

  useEffect(() => {
    const onMonthly =
      location.pathname === '/resume-marketing/monthly-performance' ||
      location.pathname.startsWith('/resume-marketing/monthly-performance/');
    const onDaily =
      location.pathname === '/resume-marketing/daily-submissions' ||
      location.pathname.startsWith('/resume-marketing/daily-submissions/');
    const onTlReports =
      user?.role?.name === 'TL' &&
      isResumeMarketingTL &&
      (location.pathname === '/tl/reports' || location.pathname.startsWith('/tl/reports/'));
    setResumeMarketingReportsOpen(onMonthly || onDaily || onTlReports);
  }, [location.pathname, isResumeMarketingTL, user?.role?.name]);

  useEffect(() => {
    if (location.pathname !== '/resume-marketing/ats-checker') return;
    if (isResumeMarketingTL || (user?.role?.name === 'Employee' && isResumeMarketingEmployee)) {
      setResumeCreationOpen(true);
    }
  }, [location.pathname, isResumeMarketingTL, isResumeMarketingEmployee, user?.role?.name]);

  const menuItems = [
    // Employee Routes - Only show generic employee links if NOT in a department with its own dashboard
    ...(user?.role?.name === 'Employee' && !isDepartmentEmployee ? [
      { path: '/employee/dashboard', icon: LayoutDashboard, label: 'Dashboard', roles: ['Employee'] },
      { path: '/employee/profile', icon: User, label: 'Profile', roles: ['Employee'] },
      { path: '/employee/attendance', icon: Calendar, label: 'Attendance', roles: ['Employee'] },
      { path: '/employee/leave', icon: FileText, label: 'Leave', roles: ['Employee'] },
      { path: '/employee/payroll', icon: IndianRupee, label: 'Payslip', roles: ['Employee'] },
      { path: '/employee/tasks', icon: CheckCircle, label: 'Tasks', roles: ['Employee'] },
      { path: '/employee/performance', icon: TrendingUp, label: 'Performance', roles: ['Employee'] },
      { path: '/employee/learning', icon: BookOpen, label: 'Learning', roles: ['Employee'] },
      { path: '/employee/assets', icon: Package, label: 'Assets', roles: ['Employee'] },
      { path: '/employee/privacy', icon: Shield, label: 'Privacy & Data', roles: ['Employee'] },
    ] : []),
    
    // Department-specific employee links (only show if employee belongs to that department)
    // Digital Marketing: attendance + dashboard + module links are defined in the dedicated block below (no profile here).
    ...(user?.role?.name === 'Employee' && isDepartmentEmployee && !isDigitalMarketingEmployee && !isDevelopmentEmployee ? [
      { path: '/employee/dashboard', icon: LayoutDashboard, label: 'Dashboard', roles: ['Employee'] },
      { path: '/employee/profile', icon: User, label: 'Profile', roles: ['Employee'] },
    ] : []),
    
    // Talent-specific links (Talent employees and Talent TLs – see and add Candidate Report & Daily Records)
    ...(user?.role?.name === 'Employee' && isTalentEmployee ? [
      { path: '/talent/candidates', icon: Users, label: 'Candidate Report', roles: ['Employee'] },
      { path: '/talent/daily-records', icon: FileText, label: 'Daily Records', roles: ['Employee'] },
      { path: '/employee/attendance', icon: Calendar, label: 'Attendance', roles: ['Employee'] },
      { path: '/employee/leave', icon: FileText, label: 'Leave', roles: ['Employee'] },
      { path: '/employee/payroll', icon: IndianRupee, label: 'Payslip', roles: ['Employee'] },
      { path: '/employee/tasks', icon: CheckCircle, label: 'Tasks', roles: ['Employee'] },
      { path: '/employee/performance', icon: TrendingUp, label: 'Performance', roles: ['Employee'] },
      { path: '/employee/learning', icon: BookOpen, label: 'Learning', roles: ['Employee'] },
      { path: '/employee/assets', icon: Package, label: 'Assets', roles: ['Employee'] },
    ] : []),
    // Talent TL – Candidate Report & Daily Records (TL role, Talent department)
    ...(isTalentTL ? [
      { path: '/talent/candidates', icon: Users, label: 'Candidate Report', roles: ['TL'] },
      { path: '/talent/daily-records', icon: FileText, label: 'Daily Records', roles: ['TL'] },
    ] : []),
    
    // Development TL: attendance, dev TL dashboard, and dev modules are rendered under Team Management (see generic TL block).
    
    // Development employees: attendance, dashboard + My Work dropdown (incl. projects) in a dedicated block below.
    
    // Digital Marketing-specific links (only for Digital Marketing employees)
    ...(user?.role?.name === 'Employee' && isDigitalMarketingEmployee ? [
      { path: '/employee/attendance', icon: Calendar, label: 'Attendance', roles: ['Employee'] },
      { path: '/employee/dashboard', icon: LayoutDashboard, label: 'Dashboard', roles: ['Employee'] },
      { path: '/digital-marketing/campaign-calendar', icon: Calendar, label: 'Campaign Calendar', roles: ['Employee'] },
      { path: '/digital-marketing/leads', icon: Users, label: 'Lead Tracking', roles: ['Employee'] },
      { path: '/digital-marketing/social-media-posts', icon: Megaphone, label: 'Social Media Planner', roles: ['Employee'] },
      { path: '/digital-marketing/seo-analytics', icon: BarChart3, label: 'SEO & Analytics', roles: ['Employee'] },
      { path: '/digital-marketing/creative-tasks', icon: Palette, label: 'Creative Tasks', roles: ['Employee'] },
    ] : []),
    
    // Technical Support-specific links (only for Technical Support employees)
    ...(user?.role?.name === 'Employee' && isTechnicalSupportEmployee ? [
      { path: '/technical-support/tickets', icon: Ticket, label: 'Ticket Management', roles: ['Employee'] },
      { path: '/technical-support/sla', icon: Clock, label: 'SLA Tracking', roles: ['Employee'] },
      { path: '/technical-support/knowledge-base', icon: BookOpen, label: 'Knowledge Base', roles: ['Employee'] },
      { path: '/technical-support/customer-history', icon: Users, label: 'Customer Issue History', roles: ['Employee'] },
      { path: '/technical-support/analytics', icon: BarChart3, label: 'Resolution Analytics', roles: ['Employee'] },
      { path: '/technical-support/escalations', icon: AlertCircle, label: 'Escalation Management', roles: ['Employee'] },
      { path: '/employee/attendance', icon: Calendar, label: 'Attendance', roles: ['Employee'] },
      { path: '/employee/leave', icon: FileText, label: 'Leave', roles: ['Employee'] },
      { path: '/employee/payroll', icon: IndianRupee, label: 'Payslip', roles: ['Employee'] },
      { path: '/employee/tasks', icon: CheckCircle, label: 'Tasks', roles: ['Employee'] },
      { path: '/employee/performance', icon: TrendingUp, label: 'Performance', roles: ['Employee'] },
      { path: '/employee/learning', icon: BookOpen, label: 'Learning', roles: ['Employee'] },
      { path: '/employee/assets', icon: Package, label: 'Assets', roles: ['Employee'] },
    ] : []),
    
    // Resume Marketing-specific links (only for Resume Marketing employees; Resume Making is TL-only)
    ...(user?.role?.name === 'Employee' && isResumeMarketingEmployee ? [
      { path: '/resume-marketing/assigned-candidates', icon: UserCheck, label: 'Assigned Candidates', roles: ['Employee'] },
      { path: '/resume-marketing/client-submissions', icon: Briefcase, label: 'Client Submissions', roles: ['Employee'] },
      { path: '/resume-marketing/interview-tracking', icon: Calendar, label: 'Interview Tracking', roles: ['Employee'] },
      { path: '/resume-marketing/resume-tracking', icon: FileText, label: 'Resume Tracking', roles: ['Employee'] },
      { path: '/resume-marketing/monthly-performance', icon: BarChart3, label: 'Monthly Performance', roles: ['Employee'] },
      { path: '/resume-marketing/daily-submissions', icon: FileText, label: 'Daily Submissions', roles: ['Employee'] },
      { path: '/employee/attendance', icon: Calendar, label: 'Attendance', roles: ['Employee'] },
      { path: '/employee/leave', icon: FileText, label: 'Leave', roles: ['Employee'] },
      { path: '/employee/payroll', icon: IndianRupee, label: 'Payslip', roles: ['Employee'] },
      { path: '/employee/tasks', icon: CheckCircle, label: 'Tasks', roles: ['Employee'] },
      { path: '/employee/performance', icon: TrendingUp, label: 'Performance', roles: ['Employee'] },
      { path: '/employee/learning', icon: BookOpen, label: 'Learning', roles: ['Employee'] },
      { path: '/employee/assets', icon: Package, label: 'Assets', roles: ['Employee'] },
    ] : []),
    
    // HR Routes
    { path: '/employee/attendance', icon: Calendar, label: 'Attendance', roles: ['HR'] },
    { path: '/hr/dashboard', icon: LayoutDashboard, label: 'HR Dashboard', roles: ['HR'] },
    { path: '/hr/approvals/leaves', icon: Calendar, label: 'Pending HR leaves', roles: ['HR'] },
    { path: '/hr/employees', icon: Users, label: 'Employees', roles: ['HR'] },
    { path: '/hr/payroll', icon: IndianRupee, label: 'Generate Payslip', roles: ['HR'] },
    { path: '/hr/salary-management', icon: IndianRupee, label: 'Salary Management', roles: ['HR'] },
    { path: '/hr/probation-tracking', icon: Clock, label: 'Probation Tracking', roles: ['HR'] },
    { path: '/hr/welcome-kit', icon: ClipboardList, label: 'Welcome Kit Automation', roles: ['HR'] },
    { path: '/hr/attendance-monitoring', icon: Activity, label: 'Attendance Monitoring', roles: ['HR'] },
    { path: '/hr/locker-management', icon: Lock, label: 'Locker Management', roles: ['HR'] },
    { path: '/hr/policies', icon: FileText, label: 'Policies', roles: ['HR'] },
    { path: '/hr/onboarding', icon: UserPlus, label: 'Onboarding', roles: ['HR'] },
    { path: '/hr/compliance', icon: Shield, label: 'Compliance', roles: ['HR'] },
    { path: '/hr/reports', icon: BarChart3, label: 'Reports', roles: ['HR'] },
    
    // Manager Routes
    { path: '/manager/dashboard', icon: LayoutDashboard, label: 'Manager Dashboard', roles: ['Manager'] },
    { path: '/manager/team', icon: Users, label: 'Team Dashboard', roles: ['Manager'] },
    { path: '/manager/approvals', icon: CheckCircle, label: 'Approvals', roles: ['Manager'] },
    { path: '/manager/performance-reviews', icon: TrendingUp, label: 'Performance Reviews', roles: ['Manager'] },
    { path: '/manager/tasks', icon: FileText, label: 'Task Management', roles: ['Manager'] },
    
    // TL /tl/* navigation: Resume & Talent TLs use their Team Management <details>; generic TLs use the same pattern in a dedicated block (not menuItems).
    { path: '/employee/leave', icon: FileText, label: 'Leave', roles: ['TL'] },
    { path: '/employee/payroll', icon: IndianRupee, label: 'Payslip', roles: ['TL'] },
    { path: '/employee/tasks', icon: CheckCircle, label: 'Tasks', roles: ['TL'] },
    { path: '/employee/performance', icon: TrendingUp, label: 'Performance', roles: ['TL'] },
    { path: '/employee/learning', icon: BookOpen, label: 'Learning', roles: ['TL'] },
    { path: '/employee/assets', icon: Package, label: 'Assets', roles: ['TL'] },
    // Marketing / Resume Marketing TL only (not Talent TL)
    ...(isMarketingTL ? [
      { path: '/resume-marketing/assigned-candidates', icon: UserCheck, label: 'Assigned Candidates', roles: ['TL'] },
    ] : []),
    
    // Finance Routes
    { path: '/finance/dashboard', icon: LayoutDashboard, label: 'Finance Dashboard', roles: ['Finance'] },
    { path: '/finance/payroll', icon: IndianRupee, label: 'Payroll Processing', roles: ['Finance'] },
    { path: '/finance/reimbursements', icon: FileText, label: 'Reimbursements', roles: ['Finance'] },
    { path: '/finance/tax-reports', icon: BarChart3, label: 'Tax Reports', roles: ['Finance'] },
    { path: '/finance/budgeting', icon: TrendingUp, label: 'Budgeting', roles: ['Finance'] },
    
    // Admin Routes
    { path: '/admin/dashboard', icon: LayoutDashboard, label: 'Admin Command Center', roles: ['Admin'] },
    { path: '/admin/employees', icon: Users, label: 'Employees', roles: ['Admin'] },
    { path: '/admin/reports', icon: BarChart3, label: 'Reports', roles: ['Admin'] },
    { path: '/admin/settings', icon: Settings, label: 'Settings', roles: ['Admin'] },

    // Admin / Super Admin — Resume Marketing & Talent (system-wide; same APIs as TL/employee with global bypass)
    ...(isGlobalAdminUser(user)
      ? [
          { path: '/resume-marketing/assigned-candidates', icon: UserCheck, label: 'RM · Assigned Candidates', roles: [GLOBAL_ADMIN_MENU_TAG] },
          { path: '/resume-marketing/client-submissions', icon: Briefcase, label: 'RM · Client Submissions', roles: [GLOBAL_ADMIN_MENU_TAG] },
          { path: '/resume-marketing/interview-tracking', icon: Calendar, label: 'RM · Interview Tracking', roles: [GLOBAL_ADMIN_MENU_TAG] },
          { path: '/resume-marketing/resume-tracking', icon: FileText, label: 'RM · Resume Tracking', roles: [GLOBAL_ADMIN_MENU_TAG] },
          { path: '/resume-marketing/monthly-performance', icon: BarChart3, label: 'RM · Monthly Performance', roles: [GLOBAL_ADMIN_MENU_TAG] },
          { path: '/resume-marketing/daily-submissions', icon: FileText, label: 'RM · Daily Submissions', roles: [GLOBAL_ADMIN_MENU_TAG] },
          { path: '/talent/candidates', icon: Users, label: 'Talent · Candidates', roles: [GLOBAL_ADMIN_MENU_TAG] },
          { path: '/talent/daily-records', icon: FileText, label: 'Talent · Daily Records', roles: [GLOBAL_ADMIN_MENU_TAG] },
          { path: '/talent/interviews', icon: ClipboardList, label: 'Talent · Interviews', roles: [GLOBAL_ADMIN_MENU_TAG] },
          { path: '/hr/dashboard', icon: LayoutDashboard, label: 'HR · Dashboard', roles: [GLOBAL_ADMIN_MENU_TAG] },
          { path: '/hr/employees', icon: Users, label: 'HR · Employees', roles: [GLOBAL_ADMIN_MENU_TAG] },
          { path: '/hr/approvals/leaves', icon: Calendar, label: 'HR · Leave approvals', roles: [GLOBAL_ADMIN_MENU_TAG] },
          { path: '/hr/attendance-monitoring', icon: Activity, label: 'HR · Attendance monitoring', roles: [GLOBAL_ADMIN_MENU_TAG] },
        ]
      : []),
    
    // Super Admin Routes
    { path: '/super-admin/dashboard', icon: LayoutDashboard, label: 'Super Admin', roles: ['Super Admin'] },
    { path: '/super-admin/employees', icon: Users, label: 'Employee Management', roles: ['Super Admin'] },
    { path: '/super-admin/departments', icon: Building2, label: 'Department Management', roles: ['Super Admin'] },
    { path: '/super-admin/system-settings', icon: Settings, label: 'System Settings', roles: ['Super Admin'] },
    { path: '/super-admin/roles', icon: Shield, label: 'Role Management', roles: ['Super Admin'] },
    { path: '/super-admin/permissions', icon: Key, label: 'Permission Management', roles: ['Super Admin'] },
    { path: '/super-admin/integrations', icon: Database, label: 'Integrations', roles: ['Super Admin'] },
    { path: '/super-admin/audit-logs', icon: Activity, label: 'Audit Logs', roles: ['Super Admin'] },
    { path: '/super-admin/reports', icon: BarChart3, label: 'Reports', roles: ['Super Admin'] },
    
    // Department Routes - Only show to NON-employee roles (HR, Manager, Admin)
    // Employees already have their department dashboard via /employee/dashboard
    { 
      path: '/talent/dashboard', 
      icon: Search, 
      label: 'Talent Dashboard', 
      roles: ['Manager', 'Admin'], // Not shown to HR
      department: 'talent',
    },
    { 
      path: '/talent/interviews', 
      icon: ClipboardList, 
      label: 'Interview & Evaluation', 
      roles: ['Manager', 'Admin', 'TL', 'Employee'],
    },
    { 
      path: '/resume-marketing/dashboard', 
      icon: FileCheck, 
      label: 'Resume Marketing Dashboard', 
      roles: ['Manager', 'Admin'], // Not shown to HR
      department: 'resume-marketing'
    },
    { 
      path: '/development/dashboard', 
      icon: Code, 
      label: 'Development Dashboard', 
      roles: ['Manager', 'Admin'], // Removed 'Employee'
      department: 'development'
    },
    { 
      path: '/digital-marketing/dashboard', 
      icon: Megaphone, 
      label: 'Digital Marketing Dashboard', 
      roles: ['Manager', 'Admin'], // Removed 'Employee'
      department: 'digital-marketing'
    },
    { 
      path: '/technical-support/dashboard', 
      icon: Headphones, 
      label: 'Technical Support Dashboard', 
      roles: ['Manager', 'Admin'], // Removed 'Employee'
      department: 'technical-support'
    },
  ];

  // Filter menu items based on role and department
  const filteredMenuItems = menuItems.filter((item) => {
    const hasRole =
      item.roles.includes(user?.role?.name || 'Employee') ||
      (isGlobalAdminUser(user) && item.roles.includes(GLOBAL_ADMIN_MENU_TAG));
    
    // For employees, hide department-specific dashboard links (they use /employee/dashboard instead)
    // Department dashboard links are only for HR, Manager, Admin roles
    if (user?.role?.name === 'Employee' && item.department) {
      return false; // Employees don't see department-specific dashboard links
    }

    // For HR, keep attendance in sidebar; other self-service links stay in Header > My Company.
    if (
      user?.role?.name === 'HR' &&
      item.path.startsWith('/employee/') &&
      item.path !== '/employee/attendance'
    ) {
      return false;
    }

    if (isResumeMarketingTL) {
      const hidePaths = new Set([
        '/employee/attendance',
        '/tl/dashboard',
        '/employee/profile',
        '/employee/leave',
        '/employee/payroll',
        '/employee/tasks',
        '/employee/performance',
        '/employee/learning',
        '/employee/assets',
        '/tl/team',
        '/tl/attendance',
        '/resume-marketing/assigned-candidates',
        '/tl/tasks',
        '/tl/approvals',
        '/tl/performance',
        '/tl/productivity',
        '/tl/reports',
        '/tl/escalations',
        '/talent/interviews',
      ]);
      if (hidePaths.has(item.path)) return false;
    }

    if (isTalentTL) {
      const hidePaths = new Set([
        '/employee/attendance',
        '/tl/dashboard',
        '/employee/profile',
        '/employee/leave',
        '/employee/payroll',
        '/employee/tasks',
        '/employee/performance',
        '/employee/learning',
        '/employee/assets',
        '/tl/team',
        '/tl/attendance',
        '/talent/candidates',
        '/talent/daily-records',
        '/tl/tasks',
        '/tl/approvals',
        '/tl/performance',
        '/tl/productivity',
        '/tl/reports',
        '/tl/escalations',
        '/talent/interviews',
      ]);
      if (hidePaths.has(item.path)) return false;
    }

    if (user?.role?.name === 'Employee' && isTalentEmployee) {
      const hidePaths = new Set([
        '/employee/attendance',
        '/employee/dashboard',
        '/employee/profile',
        '/employee/leave',
        '/employee/payroll',
        '/employee/tasks',
        '/employee/performance',
        '/employee/learning',
        '/employee/assets',
        '/talent/candidates',
        '/talent/daily-records',
        '/talent/interviews',
      ]);
      if (hidePaths.has(item.path)) return false;
    }

    if (user?.role?.name === 'Employee' && isResumeMarketingEmployee) {
      const hidePaths = new Set([
        '/employee/attendance',
        '/employee/dashboard',
        '/employee/profile',
        '/employee/leave',
        '/employee/payroll',
        '/employee/tasks',
        '/employee/performance',
        '/employee/learning',
        '/employee/assets',
        '/resume-marketing/client-submissions',
        '/resume-marketing/assigned-candidates',
        '/resume-marketing/interview-tracking',
        '/resume-marketing/monthly-performance',
        '/resume-marketing/daily-submissions',
        '/talent/interviews',
      ]);
      if (hidePaths.has(item.path)) return false;
    }

    if (user?.role?.name === 'Employee' && isDigitalMarketingEmployee) {
      const hidePaths = new Set([
        '/employee/profile',
        '/employee/leave',
        '/employee/payroll',
        '/employee/tasks',
        '/employee/performance',
        '/employee/learning',
        '/employee/assets',
      ]);
      if (hidePaths.has(item.path)) return false;
    }

    if (user?.role?.name === 'Employee' && isDevelopmentEmployee) {
      const hidePaths = new Set([
        '/employee/profile',
        '/employee/leave',
        '/employee/payroll',
        '/employee/tasks',
        '/employee/performance',
        '/employee/learning',
        '/employee/assets',
        '/employee/attendance',
        '/employee/dashboard',
        '/development/projects',
        '/development/sprints',
        '/development/bugs',
        '/development/code-reviews',
        '/development/deployments',
      ]);
      if (hidePaths.has(item.path)) return false;
    }

    if (isDevelopmentTL) {
      const hidePaths = new Set([
        '/employee/profile',
        '/employee/leave',
        '/employee/payroll',
        '/employee/tasks',
        '/employee/performance',
        '/employee/learning',
        '/employee/assets',
      ]);
      if (hidePaths.has(item.path)) return false;
    }

    if (isGenericTL && item.path === '/talent/interviews') {
      return false;
    }
    
    return hasRole;
  });

  const isDeptDependentRole = user?.role?.name === 'Employee' || user?.role?.name === 'TL';
  const showSidebarLoading = isDeptDependentRole && !departmentResolved;
  const isEnterpriseAdminView = isGlobalAdminUser(user) || user?.role?.name === 'Admin';
  const adminGroupedPaths = {
    adminManagement: ['/admin/dashboard', '/admin/employees', '/admin/reports', '/admin/settings'],
    resumeMarketing: [
      '/resume-marketing/assigned-candidates',
      '/resume-marketing/client-submissions',
      '/resume-marketing/interview-tracking',
      '/resume-marketing/resume-tracking',
      '/resume-marketing/monthly-performance',
      '/resume-marketing/daily-submissions',
    ],
    talentTeam: [
      '/talent/candidates',
      '/talent/daily-records',
      '/talent/interviews',
      '/talent/dashboard',
    ],
    hrManagement: ['/hr/dashboard', '/hr/employees', '/hr/approvals/leaves', '/hr/attendance-monitoring'],
  };

  const adminSectionConfig = [
    { key: 'adminManagement', label: 'Admin Management' },
    { key: 'resumeMarketing', label: 'Resume Marketing' },
    { key: 'talentTeam', label: 'Talent Team' },
    { key: 'hrManagement', label: 'HR Management' },
  ];

  const getGroupedItems = (paths) =>
    paths
      .map((path) => filteredMenuItems.find((item) => item.path === path))
      .filter(Boolean);

  const groupedAdminItems = {
    adminManagement: getGroupedItems(adminGroupedPaths.adminManagement),
    resumeMarketing: getGroupedItems(adminGroupedPaths.resumeMarketing),
    talentTeam: getGroupedItems(adminGroupedPaths.talentTeam),
    hrManagement: getGroupedItems(adminGroupedPaths.hrManagement),
  };
  const groupedAdminPathSet = new Set(Object.values(adminGroupedPaths).flat());
  const ungroupedAdminItems = filteredMenuItems.filter((item) => !groupedAdminPathSet.has(item.path));

  useEffect(() => {
    if (!isEnterpriseAdminView) return;
    setAdminGroupOpen((prev) => {
      const nextOpen = { ...prev };
      adminSectionConfig.forEach((section) => {
        const hasActive = groupedAdminItems[section.key].some((item) => isPathActive(item.path));
        if (hasActive) nextOpen[section.key] = true;
      });
      return nextOpen;
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.pathname, isEnterpriseAdminView]);

  const handleSidebarLogout = () => {
    dispatch(logout());
    navigate('/login');
  };

  const renderSidebarLink = (item, options = {}) => {
    const Icon = item.icon;
    const isActive = isPathActive(item.path);
    const itemLabel = options.label || item.label;
    return (
      <Link
        key={options.key || item.path}
        to={item.path}
        className={`mx-2 my-0.5 flex items-center gap-3 rounded-xl px-3 py-2.5 text-sm transition-colors ${
          isActive ? 'bg-blue-50 font-medium text-blue-700 ring-1 ring-blue-100' : 'text-content-secondary hover:bg-gray-100'
        }`}
        title={itemLabel}
      >
        <Icon className={`h-5 w-5 shrink-0 ${isActive ? 'text-blue-600' : 'text-content-muted'}`} />
        <span className="min-w-0 flex-1 truncate">{itemLabel}</span>
      </Link>
    );
  };

  const renderAdminSection = (sectionKey, sectionLabel) => {
    const items = groupedAdminItems[sectionKey];
    if (!items?.length) return null;

    const withTalentEvalAlias =
      sectionKey === 'talentTeam'
        ? [
            ...items,
            ...(items.some((item) => item.path === '/talent/interviews')
              ? [{ ...items.find((item) => item.path === '/talent/interviews'), _aliasLabel: 'Interview & Evaluation', _aliasKey: 'talent-interview-eval' }]
              : []),
          ]
        : items;

    return (
      <details
        key={sectionKey}
        className="mx-2 mt-4 border border-default/80 rounded-xl bg-white/50"
        open={adminGroupOpen[sectionKey]}
        onToggle={(e) => {
          const isOpen = e.currentTarget.open;
          setAdminGroupOpen((prev) => ({ ...prev, [sectionKey]: isOpen }));
        }}
      >
        <summary className="flex list-none cursor-pointer items-center gap-2 rounded-xl px-3 py-2.5 text-[11px] font-semibold uppercase tracking-[0.12em] text-content-muted transition-colors hover:bg-slate-100/80 [&::-webkit-details-marker]:hidden">
          <span className="truncate">{sectionLabel}</span>
          <ChevronDown className={`ml-auto h-4 w-4 shrink-0 transition-transform ${adminGroupOpen[sectionKey] ? 'rotate-180' : ''}`} />
        </summary>
        <div className="pb-2">
          {withTalentEvalAlias.map((item) => renderSidebarLink(item, { key: item._aliasKey || item.path, label: item._aliasLabel || item.label }))}
        </div>
      </details>
    );
  };

  return (
    <aside className="w-64 flex h-screen shrink-0 flex-col border-r border-[var(--border-color)] bg-slate-50/70 hrms-sidebar md:relative">
      <div className="sidebar-logo-wrapper sticky top-0 z-20 flex h-16 shrink-0 items-center border-b border-[var(--border-color)] bg-[var(--bg-surface)]/95 px-4 backdrop-blur">
        <div className="logo-visibility-shell inline-flex items-center justify-start">
          <BrandLogo variant="sidebar" />
        </div>
      </div>
      <nav className={`sidebar-nav flex-1 min-h-0 overflow-y-auto overflow-x-hidden py-2 ${breakLocked ? 'pointer-events-none' : ''}`}>
        {showSidebarLoading && (
          <div className="mx-3 my-2 px-3 py-2 text-xs rounded-md bg-slate-100 text-content-secondary border border-slate-200">
            Loading menu...
          </div>
        )}
        {!showSidebarLoading && (
          <>
        {breakLocked && (
          <div className="mx-3 mb-2 px-3 py-2 text-xs rounded-md bg-amber-100 text-amber-900 border border-amber-300">
            Break is active. End break from header to unlock navigation.
          </div>
        )}
        {isResumeMarketingTL && (
          <>
            <Link
              to="/employee/attendance"
              className={`flex items-center px-4 py-2.5 mx-2 rounded-lg text-content-secondary hover:bg-gray-100 transition-colors ${
                isPathActive('/employee/attendance')
                  ? 'bg-blue-50 text-blue-700 font-medium border-l-4 border-l-blue-600'
                  : ''
              }`}
            >
              <Calendar className={`w-5 h-5 mr-3 flex-shrink-0 ${isPathActive('/employee/attendance') ? 'text-blue-600' : 'text-content-muted'}`} />
              <span className="truncate">Attendance</span>
            </Link>

            <Link
              to="/tl/dashboard"
              className={`flex items-center px-4 py-2.5 mx-2 rounded-lg text-content-secondary hover:bg-gray-100 transition-colors ${
                isPathActive('/tl/dashboard') || isPathActive('/resume-marketing/dashboard')
                  ? 'bg-blue-50 text-blue-700 font-medium border-l-4 border-l-blue-600'
                  : ''
              }`}
            >
              <LayoutDashboard className={`w-5 h-5 mr-3 flex-shrink-0 ${isPathActive('/tl/dashboard') || isPathActive('/resume-marketing/dashboard') ? 'text-blue-600' : 'text-content-muted'}`} />
              <span className="truncate">Marketing Command Center</span>
            </Link>

            <ResumeCreationSidebarDropdown
              variant="tl"
              returnPath="/tl/dashboard"
              open={resumeCreationOpen}
              onToggle={setResumeCreationOpen}
            />

            <details className="mx-2 mt-3" open={resumeMyWorkOpen} onToggle={(e) => setResumeMyWorkOpen(e.currentTarget.open)}>
              <summary className="cursor-pointer flex items-center px-4 py-2.5 rounded-lg text-content-secondary hover:bg-gray-100 transition-colors">
                <ClipboardList className="w-5 h-5 mr-3 flex-shrink-0 text-content-muted" />
                <span className="truncate">MY WORK</span>
                <ChevronDown className={`w-4 h-4 ml-auto text-content-muted transition-transform ${resumeMyWorkOpen ? 'rotate-180' : ''}`} />
              </summary>
              <div className="ml-6 mt-1 space-y-1">
                {[
                  { path: '/employee/profile', icon: User, label: 'Profile' },
                  { path: '/resume-marketing/client-submissions', icon: Briefcase, label: 'Client Submissions' },
                  { path: '/resume-marketing/interview-tracking', icon: Calendar, label: 'Interview Tracking' },
                ].map((item) => {
                  const Icon = item.icon;
                  const isActive = isPathActive(item.path);
                  return (
                    <Link key={item.path} to={item.path} className={`flex items-center px-4 py-2.5 rounded-lg text-content-secondary hover:bg-gray-100 transition-colors ${isActive ? 'bg-blue-50 text-blue-700 font-medium border-l-4 border-l-blue-600' : ''}`}>
                      <Icon className={`w-5 h-5 mr-3 flex-shrink-0 ${isActive ? 'text-blue-600' : 'text-content-muted'}`} />
                      <span className="truncate">{item.label}</span>
                    </Link>
                  );
                })}
              </div>
            </details>


            <details className="mx-2 mt-3" open={resumeTeamMgmtOpen} onToggle={(e) => setResumeTeamMgmtOpen(e.currentTarget.open)}>
              <summary className="cursor-pointer flex items-center px-4 py-2.5 rounded-lg text-content-secondary hover:bg-gray-100 transition-colors">
                <Users className="w-5 h-5 mr-3 flex-shrink-0 text-content-muted" />
                <span className="truncate">Team Management</span>
                <ChevronDown className={`w-4 h-4 ml-auto text-content-muted transition-transform ${resumeTeamMgmtOpen ? 'rotate-180' : ''}`} />
              </summary>
              <div className="ml-6 mt-1 space-y-1">
                {[
                  { path: '/tl/team', icon: Users, label: 'Team' },
                  { path: '/tl/attendance', icon: Calendar, label: 'Team Attendance' },
                  { path: '/resume-marketing/assigned-candidates', icon: UserCheck, label: 'Assigned Candidates' },
                  { path: '/resume-marketing/resume-tracking', icon: FileText, label: 'Resume Tracking' },
                  { path: '/tl/tasks', icon: FileText, label: 'Task Management' },
                  { path: '/tl/approvals', icon: CheckCircle, label: 'Approvals' },
                  { path: '/tl/performance', icon: TrendingUp, label: 'Performance Feedback' },
                  { path: '/tl/productivity', icon: BarChart3, label: 'Productivity Dashboard' },
                  { path: '/tl/escalations', icon: AlertCircle, label: 'Escalations' },
                ].map((item) => {
                  const Icon = item.icon;
                  const isActive = isPathActive(item.path);
                  return (
                    <Link key={item.path} to={item.path} className={`flex items-center px-4 py-2.5 rounded-lg text-content-secondary hover:bg-gray-100 transition-colors ${isActive ? 'bg-blue-50 text-blue-700 font-medium border-l-4 border-l-blue-600' : ''}`}>
                      <Icon className={`w-5 h-5 mr-3 flex-shrink-0 ${isActive ? 'text-blue-600' : 'text-content-muted'}`} />
                      <span className="truncate">{item.label}</span>
                    </Link>
                  );
                })}
              </div>
            </details>

            <details
              className="mx-2 mt-2"
              open={resumeMarketingReportsOpen}
              onToggle={(e) => setResumeMarketingReportsOpen(e.currentTarget.open)}
            >
              <summary className="cursor-pointer flex items-center px-4 py-2.5 rounded-lg text-content-secondary hover:bg-gray-100 transition-colors">
                <BarChart3 className="w-5 h-5 mr-3 flex-shrink-0 text-content-muted" />
                <span className="truncate">Reports</span>
                <ChevronDown className={`w-4 h-4 ml-auto text-content-muted transition-transform ${resumeMarketingReportsOpen ? 'rotate-180' : ''}`} />
              </summary>
              <div className="ml-6 mt-1 space-y-1">
                {[
                  { path: '/resume-marketing/monthly-performance', icon: BarChart3, label: 'Monthly submission' },
                  { path: '/resume-marketing/daily-submissions', icon: FileText, label: 'Daily submission' },
                  { path: '/tl/reports', icon: BarChart3, label: 'Report generation' },
                ].map((item) => {
                  const Icon = item.icon;
                  const isActive = isPathActive(item.path);
                  return (
                    <Link
                      key={item.path}
                      to={item.path}
                      className={`flex items-center px-4 py-2.5 rounded-lg text-content-secondary hover:bg-gray-100 transition-colors ${
                        isActive ? 'bg-blue-50 text-blue-700 font-medium border-l-4 border-l-blue-600' : ''
                      }`}
                    >
                      <Icon className={`w-5 h-5 mr-3 flex-shrink-0 ${isActive ? 'text-blue-600' : 'text-content-muted'}`} />
                      <span className="truncate">{item.label}</span>
                    </Link>
                  );
                })}
              </div>
            </details>
          </>
        )}
        {isTalentTL && (
          <>
            <Link
              to="/employee/attendance"
              className={`flex items-center px-4 py-2.5 mx-2 rounded-lg text-content-secondary hover:bg-gray-100 transition-colors ${
                isPathActive('/employee/attendance')
                  ? 'bg-blue-50 text-blue-700 font-medium border-l-4 border-l-blue-600'
                  : ''
              }`}
            >
              <Calendar className={`w-5 h-5 mr-3 flex-shrink-0 ${isPathActive('/employee/attendance') ? 'text-blue-600' : 'text-content-muted'}`} />
              <span className="truncate">Attendance</span>
            </Link>

            <Link
              to="/tl/dashboard"
              className={`flex items-center px-4 py-2.5 mx-2 rounded-lg text-content-secondary hover:bg-gray-100 transition-colors ${
                isPathActive('/tl/dashboard') || isPathActive('/talent/dashboard') || isPathActive('/employee/dashboard')
                  ? 'bg-blue-50 text-blue-700 font-medium border-l-4 border-l-blue-600'
                  : ''
              }`}
            >
              <LayoutDashboard className={`w-5 h-5 mr-3 flex-shrink-0 ${isPathActive('/tl/dashboard') || isPathActive('/talent/dashboard') || isPathActive('/employee/dashboard') ? 'text-blue-600' : 'text-content-muted'}`} />
              <span className="truncate">Talent Command Center</span>
            </Link>

            <details className="mx-2 mt-3" open={talentMyWorkOpen} onToggle={(e) => setTalentMyWorkOpen(e.currentTarget.open)}>
              <summary className="cursor-pointer flex items-center px-4 py-2.5 rounded-lg text-content-secondary hover:bg-gray-100 transition-colors">
                <ClipboardList className="w-5 h-5 mr-3 flex-shrink-0 text-content-muted" />
                <span className="truncate">MY WORK</span>
                <ChevronDown className={`w-4 h-4 ml-auto text-content-muted transition-transform ${talentMyWorkOpen ? 'rotate-180' : ''}`} />
              </summary>
              <div className="ml-6 mt-1 space-y-1">
                {[
                  { path: '/employee/profile', icon: User, label: 'Profile' },
                  { path: '/talent/candidates', icon: Users, label: 'Candidate Reports' },
                  { path: '/talent/daily-records', icon: FileText, label: 'Daily Records' },
                ].map((item) => {
                  const Icon = item.icon;
                  const isActive = isPathActive(item.path);
                  return (
                    <Link key={item.path} to={item.path} className={`flex items-center px-4 py-2.5 rounded-lg text-content-secondary hover:bg-gray-100 transition-colors ${isActive ? 'bg-blue-50 text-blue-700 font-medium border-l-4 border-l-blue-600' : ''}`}>
                      <Icon className={`w-5 h-5 mr-3 flex-shrink-0 ${isActive ? 'text-blue-600' : 'text-content-muted'}`} />
                      <span className="truncate">{item.label}</span>
                    </Link>
                  );
                })}
              </div>
            </details>


            <details className="mx-2 mt-3" open={talentTeamMgmtOpen} onToggle={(e) => setTalentTeamMgmtOpen(e.currentTarget.open)}>
              <summary className="cursor-pointer flex items-center px-4 py-2.5 rounded-lg text-content-secondary hover:bg-gray-100 transition-colors">
                <Users className="w-5 h-5 mr-3 flex-shrink-0 text-content-muted" />
                <span className="truncate">Team Management</span>
                <ChevronDown className={`w-4 h-4 ml-auto text-content-muted transition-transform ${talentTeamMgmtOpen ? 'rotate-180' : ''}`} />
              </summary>
              <div className="ml-6 mt-1 space-y-1">
                {[
                  { path: '/tl/team', icon: Users, label: 'Team' },
                  { path: '/tl/attendance', icon: Calendar, label: 'Team Attendance' },
                  { path: '/tl/tasks', icon: FileText, label: 'Task Management' },
                  { path: '/tl/approvals', icon: CheckCircle, label: 'Approvals' },
                  { path: '/tl/performance', icon: TrendingUp, label: 'Performance Feedback' },
                  { path: '/tl/escalations', icon: AlertCircle, label: 'Escalations' },
                  { path: '/tl/productivity', icon: BarChart3, label: 'Productivity Dashboard' },
                  { path: '/tl/reports', icon: BarChart3, label: 'Reports' },
                  { path: '/talent/interviews', icon: ClipboardList, label: 'Interview & Evaluation' },
                ].map((item) => {
                  const Icon = item.icon;
                  const isActive = isPathActive(item.path);
                  return (
                    <Link key={item.path} to={item.path} className={`flex items-center px-4 py-2.5 rounded-lg text-content-secondary hover:bg-gray-100 transition-colors ${isActive ? 'bg-blue-50 text-blue-700 font-medium border-l-4 border-l-blue-600' : ''}`}>
                      <Icon className={`w-5 h-5 mr-3 flex-shrink-0 ${isActive ? 'text-blue-600' : 'text-content-muted'}`} />
                      <span className="truncate">{item.label}</span>
                    </Link>
                  );
                })}
              </div>
            </details>
          </>
        )}
        {isGenericTL && (
          <>
            <Link
              to="/employee/attendance"
              className={`flex items-center px-4 py-2.5 mx-2 rounded-lg text-content-secondary hover:bg-gray-100 transition-colors ${
                isPathActive('/employee/attendance')
                  ? 'bg-blue-50 text-blue-700 font-medium border-l-4 border-l-blue-600'
                  : ''
              }`}
            >
              <Calendar className={`w-5 h-5 mr-3 flex-shrink-0 ${isPathActive('/employee/attendance') ? 'text-blue-600' : 'text-content-muted'}`} />
              <span className="truncate">Attendance</span>
            </Link>

            {isDevelopmentTL ? (
              <Link
                to="/development/tl-dashboard"
                className={`flex items-center px-4 py-2.5 mx-2 rounded-lg text-content-secondary hover:bg-gray-100 transition-colors ${
                  isPathActive('/development/tl-dashboard')
                    ? 'bg-blue-50 text-blue-700 font-medium border-l-4 border-l-blue-600'
                    : ''
                }`}
              >
                <LayoutDashboard className={`w-5 h-5 mr-3 flex-shrink-0 ${isPathActive('/development/tl-dashboard') ? 'text-blue-600' : 'text-content-muted'}`} />
                <span className="truncate">Development TL Dashboard</span>
              </Link>
            ) : (
              <Link
                to="/tl/dashboard"
                className={`flex items-center px-4 py-2.5 mx-2 rounded-lg text-content-secondary hover:bg-gray-100 transition-colors ${
                  isPathActive('/tl/dashboard')
                    ? 'bg-blue-50 text-blue-700 font-medium border-l-4 border-l-blue-600'
                    : ''
                }`}
              >
                <LayoutDashboard className={`w-5 h-5 mr-3 flex-shrink-0 ${isPathActive('/tl/dashboard') ? 'text-blue-600' : 'text-content-muted'}`} />
                <span className="truncate">TL Dashboard</span>
              </Link>
            )}

            <details
              className="mx-2 mt-3"
              open={genericTlTeamMgmtOpen}
              onToggle={(e) => setGenericTlTeamMgmtOpen(e.currentTarget.open)}
            >
              <summary className="cursor-pointer flex items-center px-4 py-2.5 rounded-lg text-content-secondary hover:bg-gray-100 transition-colors">
                <Users className="w-5 h-5 mr-3 flex-shrink-0 text-content-muted" />
                <span className="truncate">Team Management</span>
                <ChevronDown className={`w-4 h-4 ml-auto text-content-muted transition-transform ${genericTlTeamMgmtOpen ? 'rotate-180' : ''}`} />
              </summary>
              <div className="ml-6 mt-1 space-y-1">
                {[
                  { path: '/tl/team', icon: Users, label: 'Team' },
                  { path: '/tl/attendance', icon: Calendar, label: 'Team Attendance' },
                  { path: '/tl/tasks', icon: FileText, label: 'Task Management' },
                  { path: '/tl/approvals', icon: CheckCircle, label: 'Approvals' },
                  { path: '/tl/performance', icon: TrendingUp, label: 'Performance Feedback' },
                  { path: '/tl/escalations', icon: AlertCircle, label: 'Escalations' },
                  { path: '/tl/productivity', icon: BarChart3, label: 'Productivity Dashboard' },
                  { path: '/tl/reports', icon: BarChart3, label: 'Reports' },
                  { path: '/talent/interviews', icon: ClipboardList, label: 'Interview & Evaluation' },
                ].map((item) => {
                  const Icon = item.icon;
                  const isActive = isPathActive(item.path);
                  return (
                    <Link
                      key={item.path}
                      to={item.path}
                      className={`flex items-center px-4 py-2.5 rounded-lg text-content-secondary hover:bg-gray-100 transition-colors ${
                        isActive ? 'bg-blue-50 text-blue-700 font-medium border-l-4 border-l-blue-600' : ''
                      }`}
                    >
                      <Icon className={`w-5 h-5 mr-3 flex-shrink-0 ${isActive ? 'text-blue-600' : 'text-content-muted'}`} />
                      <span className="truncate">{item.label}</span>
                    </Link>
                  );
                })}
              </div>
            </details>

            {isDevelopmentTL && (
                <details
                  className="mx-2 mt-3"
                  open={devMyWorkOpen}
                  onToggle={(e) => setDevMyWorkOpen(e.currentTarget.open)}
                >
                  <summary className="cursor-pointer flex items-center px-4 py-2.5 rounded-lg text-content-secondary hover:bg-gray-100 transition-colors">
                    <ClipboardList className="w-5 h-5 mr-3 flex-shrink-0 text-content-muted" />
                    <span className="truncate">My Work</span>
                    <ChevronDown className={`w-4 h-4 ml-auto text-content-muted transition-transform ${devMyWorkOpen ? 'rotate-180' : ''}`} />
                  </summary>
                  <div className="ml-6 mt-1 space-y-1">
                    {[
                      { path: '/development/projects', icon: FolderKanban, label: 'Projects' },
                      { path: '/development/sprints', icon: GitBranch, label: 'Sprints' },
                      { path: '/development/bugs', icon: Bug, label: 'Bugs' },
                      { path: '/development/code-reviews', icon: Code, label: 'Code Reviews' },
                      { path: '/development/deployments', icon: Rocket, label: 'Deployments' },
                    ].map((item) => {
                      const Icon = item.icon;
                      const isActive = isPathActive(item.path);
                      return (
                        <Link
                          key={item.path}
                          to={item.path}
                          className={`flex items-center px-4 py-2.5 rounded-lg text-content-secondary hover:bg-gray-100 transition-colors ${
                            isActive ? 'bg-blue-50 text-blue-700 font-medium border-l-4 border-l-blue-600' : ''
                          }`}
                        >
                          <Icon className={`w-5 h-5 mr-3 flex-shrink-0 ${isActive ? 'text-blue-600' : 'text-content-muted'}`} />
                          <span className="truncate">{item.label}</span>
                        </Link>
                      );
                    })}
                  </div>
                </details>
            )}
          </>
        )}
        {user?.role?.name === 'Employee' && isResumeMarketingEmployee && (
          <>
            <Link
              to="/employee/attendance"
              className={`mx-2 my-0.5 flex items-center gap-3 rounded-xl px-3 py-2.5 text-sm transition-colors ${
                isPathActive('/employee/attendance')
                  ? 'bg-blue-50 font-medium text-blue-600 ring-1 ring-blue-100'
                  : 'text-content-secondary hover:bg-gray-100'
              }`}
            >
              <Calendar className={`h-5 w-5 shrink-0 ${isPathActive('/employee/attendance') ? 'text-blue-600' : 'text-content-muted'}`} />
              <span className="truncate">Attendance</span>
            </Link>

            <Link
              to="/employee/dashboard"
              className={`mx-2 my-0.5 flex items-center gap-3 rounded-xl px-3 py-2.5 text-sm transition-colors ${
                isPathActive('/employee/dashboard')
                  ? 'bg-blue-50 font-medium text-blue-600 ring-1 ring-blue-100'
                  : 'text-content-secondary hover:bg-gray-100'
              }`}
            >
              <LayoutDashboard className={`h-5 w-5 shrink-0 ${isPathActive('/employee/dashboard') ? 'text-blue-600' : 'text-content-muted'}`} />
              <span className="truncate">Employee dashboard</span>
            </Link>

            <p className="mb-1 mt-4 px-4 text-[11px] font-semibold uppercase tracking-wider text-content-muted">
              Recruitment
            </p>

            <ResumeCreationSidebarDropdown
              returnPath="/resume-marketing/dashboard"
              open={resumeCreationOpen}
              onToggle={setResumeCreationOpen}
            />

            <Link
              to="/resume-marketing/dashboard"
              className={`mx-2 my-0.5 flex items-center gap-3 rounded-xl px-3 py-2.5 text-sm transition-colors ${
                isPathActive('/resume-marketing/dashboard')
                  ? 'bg-blue-50 font-medium text-blue-600 ring-1 ring-blue-100'
                  : 'text-content-secondary hover:bg-gray-100'
              }`}
            >
              <FileCheck className={`h-5 w-5 shrink-0 ${isPathActive('/resume-marketing/dashboard') ? 'text-blue-600' : 'text-content-muted'}`} />
              <span className="truncate">Resume marketing dashboard</span>
            </Link>

            {[
              { path: '/resume-marketing/assigned-candidates', icon: UserCheck, label: 'Assigned candidates' },
              { path: '/resume-marketing/client-submissions', icon: Briefcase, label: 'Client submissions' },
              { path: '/resume-marketing/interview-tracking', icon: Calendar, label: 'Interview tracking' },
              { path: '/resume-marketing/resume-tracking', icon: FileText, label: 'Resume tracking' },
            ].map((item) => {
              const Icon = item.icon;
              const isActive = isPathActive(item.path);
              return (
                <Link
                  key={item.path}
                  to={item.path}
                  className={`mx-2 my-0.5 flex items-center gap-3 rounded-xl px-3 py-2.5 text-sm transition-colors ${
                    isActive ? 'bg-blue-50 font-medium text-blue-600 ring-1 ring-blue-100' : 'text-content-secondary hover:bg-gray-100'
                  }`}
                >
                  <Icon className={`h-5 w-5 shrink-0 ${isActive ? 'text-blue-600' : 'text-content-muted'}`} />
                  <span className="truncate">{item.label}</span>
                </Link>
              );
            })}

            <details
              className="mx-2 mt-3"
              open={resumeMarketingReportsOpen}
              onToggle={(e) => setResumeMarketingReportsOpen(e.currentTarget.open)}
            >
              <summary className="flex cursor-pointer list-none items-center gap-3 rounded-xl px-3 py-2.5 text-sm text-content-secondary transition-colors hover:bg-gray-100 [&::-webkit-details-marker]:hidden">
                <BarChart3 className="h-5 w-5 shrink-0 text-content-muted" />
                <span className="truncate font-medium">Reports</span>
                <ChevronDown
                  className={`ml-auto h-4 w-4 shrink-0 text-content-muted transition-transform ${resumeMarketingReportsOpen ? 'rotate-180' : ''}`}
                />
              </summary>
              <div className="ml-2 mt-1 space-y-0.5 border-l border-default/80 pl-3">
                {[
                  { path: '/resume-marketing/monthly-performance', icon: BarChart3, label: 'Monthly performance' },
                  { path: '/resume-marketing/daily-submissions', icon: FileText, label: 'Daily submissions' },
                ].map((item) => {
                  const Icon = item.icon;
                  const isActive = isPathActive(item.path);
                  return (
                    <Link
                      key={item.path}
                      to={item.path}
                      className={`my-0.5 flex items-center gap-3 rounded-lg px-3 py-2 text-sm transition-colors ${
                        isActive ? 'bg-blue-50 font-medium text-blue-600 ring-1 ring-blue-100' : 'text-content-secondary hover:bg-gray-100'
                      }`}
                    >
                      <Icon className={`h-4 w-4 shrink-0 ${isActive ? 'text-blue-600' : 'text-content-muted'}`} />
                      <span className="truncate">{item.label}</span>
                    </Link>
                  );
                })}
              </div>
            </details>
          </>
        )}
        {user?.role?.name === 'Employee' && isTalentEmployee && (
          <>
            <Link
              to="/employee/attendance"
              className={`flex items-center px-4 py-2.5 mx-2 rounded-lg text-content-secondary hover:bg-gray-100 transition-colors ${
                isPathActive('/employee/attendance')
                  ? 'bg-blue-50 text-blue-700 font-medium border-l-4 border-l-blue-600'
                  : ''
              }`}
            >
              <Calendar className={`w-5 h-5 mr-3 flex-shrink-0 ${isPathActive('/employee/attendance') ? 'text-blue-600' : 'text-content-muted'}`} />
              <span className="truncate">Attendance</span>
            </Link>

            <Link
              to="/employee/dashboard"
              className={`flex items-center px-4 py-2.5 mx-2 rounded-lg text-content-secondary hover:bg-gray-100 transition-colors ${
                isPathActive('/employee/dashboard') || isPathActive('/talent/dashboard')
                  ? 'bg-blue-50 text-blue-700 font-medium border-l-4 border-l-blue-600'
                  : ''
              }`}
            >
              <LayoutDashboard className={`w-5 h-5 mr-3 flex-shrink-0 ${isPathActive('/employee/dashboard') || isPathActive('/talent/dashboard') ? 'text-blue-600' : 'text-content-muted'}`} />
              <span className="truncate">Talent Command Center</span>
            </Link>


            <Link
              to="/talent/candidates"
              className={`flex items-center px-4 py-2.5 mx-2 rounded-lg text-content-secondary hover:bg-gray-100 transition-colors ${
                isPathActive('/talent/candidates')
                  ? 'bg-blue-50 text-blue-700 font-medium border-l-4 border-l-blue-600'
                  : ''
              }`}
            >
              <Users className={`w-5 h-5 mr-3 flex-shrink-0 ${isPathActive('/talent/candidates') ? 'text-blue-600' : 'text-content-muted'}`} />
              <span className="truncate">Candidate Report</span>
            </Link>

            <Link
              to="/talent/daily-records"
              className={`flex items-center px-4 py-2.5 mx-2 rounded-lg text-content-secondary hover:bg-gray-100 transition-colors ${
                isPathActive('/talent/daily-records')
                  ? 'bg-blue-50 text-blue-700 font-medium border-l-4 border-l-blue-600'
                  : ''
              }`}
            >
              <FileText className={`w-5 h-5 mr-3 flex-shrink-0 ${isPathActive('/talent/daily-records') ? 'text-blue-600' : 'text-content-muted'}`} />
              <span className="truncate">Daily Records</span>
            </Link>
          </>
        )}
        {user?.role?.name === 'Employee' && isDevelopmentEmployee && (
          <>
            <Link
              to="/employee/attendance"
              className={`flex items-center px-4 py-2.5 mx-2 rounded-lg text-content-secondary hover:bg-gray-100 transition-colors ${
                isPathActive('/employee/attendance')
                  ? 'bg-blue-50 text-blue-700 font-medium border-l-4 border-l-blue-600'
                  : ''
              }`}
            >
              <Calendar className={`w-5 h-5 mr-3 flex-shrink-0 ${isPathActive('/employee/attendance') ? 'text-blue-600' : 'text-content-muted'}`} />
              <span className="truncate">Attendance</span>
            </Link>

            <Link
              to="/employee/dashboard"
              className={`flex items-center px-4 py-2.5 mx-2 rounded-lg text-content-secondary hover:bg-gray-100 transition-colors ${
                isPathActive('/employee/dashboard')
                  ? 'bg-blue-50 text-blue-700 font-medium border-l-4 border-l-blue-600'
                  : ''
              }`}
            >
              <LayoutDashboard className={`w-5 h-5 mr-3 flex-shrink-0 ${isPathActive('/employee/dashboard') ? 'text-blue-600' : 'text-content-muted'}`} />
              <span className="truncate">Dashboard</span>
            </Link>

            <details
              className="mx-2 mt-3"
              open={devMyWorkOpen}
              onToggle={(e) => setDevMyWorkOpen(e.currentTarget.open)}
            >
              <summary className="cursor-pointer flex items-center px-4 py-2.5 rounded-lg text-content-secondary hover:bg-gray-100 transition-colors">
                <ClipboardList className="w-5 h-5 mr-3 flex-shrink-0 text-content-muted" />
                <span className="truncate">My Work</span>
                <ChevronDown className={`w-4 h-4 ml-auto text-content-muted transition-transform ${devMyWorkOpen ? 'rotate-180' : ''}`} />
              </summary>
              <div className="ml-6 mt-1 space-y-1">
                {[
                  { path: '/development/projects', icon: FolderKanban, label: 'Projects' },
                  { path: '/development/sprints', icon: GitBranch, label: 'Sprints' },
                  { path: '/development/bugs', icon: Bug, label: 'Bugs' },
                  { path: '/development/code-reviews', icon: Code, label: 'Code Reviews' },
                  { path: '/development/deployments', icon: Rocket, label: 'Deployments' },
                ].map((item) => {
                  const Icon = item.icon;
                  const isActive = isPathActive(item.path);
                  return (
                    <Link
                      key={item.path}
                      to={item.path}
                      className={`flex items-center px-4 py-2.5 rounded-lg text-content-secondary hover:bg-gray-100 transition-colors ${
                        isActive ? 'bg-blue-50 text-blue-700 font-medium border-l-4 border-l-blue-600' : ''
                      }`}
                    >
                      <Icon className={`w-5 h-5 mr-3 flex-shrink-0 ${isActive ? 'text-blue-600' : 'text-content-muted'}`} />
                      <span className="truncate">{item.label}</span>
                    </Link>
                  );
                })}
              </div>
            </details>
          </>
        )}
        {isEnterpriseAdminView ? (
          <div className="pb-4">
            {adminSectionConfig.map((section) => renderAdminSection(section.key, section.label))}
            {ungroupedAdminItems.length > 0 && (
              <details
                className="mx-2 mt-4 border border-default/80 rounded-xl bg-white/50"
                open={false}
              >
                <summary className="flex list-none cursor-pointer items-center gap-2 rounded-xl px-3 py-2.5 text-[11px] font-semibold uppercase tracking-[0.12em] text-content-muted transition-colors hover:bg-slate-100/80 [&::-webkit-details-marker]:hidden">
                  <span className="truncate">Additional</span>
                  <ChevronDown className="ml-auto h-4 w-4 shrink-0 text-content-muted" />
                </summary>
                <div className="pb-2">
                  {ungroupedAdminItems.map((item) => renderSidebarLink(item))}
                </div>
              </details>
            )}
          </div>
        ) : (
          filteredMenuItems.map((item) => {
            const Icon = item.icon;
            const isActive = isPathActive(item.path);
            return (
              <Link
                key={item.path}
                to={item.path}
                className={`flex items-center px-4 py-2.5 mx-2 rounded-lg text-content-secondary hover:bg-gray-100 transition-colors ${
                  isActive ? 'bg-blue-50 text-blue-700 font-medium border-l-4 border-l-blue-600' : ''
                }`}
                title={item.label}
              >
                <Icon className={`w-5 h-5 mr-3 flex-shrink-0 ${isActive ? 'text-blue-600' : 'text-content-muted'}`} />
                <span className="min-w-0 truncate">{item.label}</span>
              </Link>
            );
          })
        )}
          </>
        )}
      </nav>
      <div className="sticky bottom-0 z-20 border-t border-[var(--border-color)] bg-[var(--bg-surface)]/95 p-2 backdrop-blur">
        <button
          type="button"
          onClick={handleSidebarLogout}
          className="flex w-full items-center gap-3 rounded-xl px-3 py-2.5 text-sm font-medium text-content-secondary transition-colors hover:bg-slate-100 hover:text-content-primary"
          title="Logout"
        >
          <LogOut className="h-5 w-5 shrink-0 text-content-muted" />
          <span className="truncate">Logout</span>
        </button>
      </div>
    </aside>
  );
};

export default Sidebar;
